import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchTerm, fetchProducts, fetchCategory, regenerateTerm, fetchRoutine } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { RoutineBuilder } from "../components/RoutineBuilder";
import { Quiz } from "../components/Quiz";
import { ChevronLeft, Sparkles, RefreshCw } from "lucide-react";

export default function Term() {
  const { slug } = useParams();
  const [term, setTerm] = useState(null);
  const [products, setProducts] = useState([]);
  const [routine, setRoutine] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  const load = () => {
    setLoading(true);
    fetchTerm(slug)
      .then(async (t) => {
        setTerm(t);
        document.title = `${t.name} – Glossaire Beckyliss`;
        const [prods, cat, rout] = await Promise.all([
          fetchProducts({ term: slug }),
          fetchCategory(t.category_slug),
          fetchRoutine(slug).catch(() => []),
        ]);
        setProducts(prods);
        setCategory(cat);
        setRoutine(rout);
      })
      .catch(() => setTerm(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const updated = await regenerateTerm(slug);
      setTerm(updated);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <div className="max-w-editorial mx-auto px-6 py-32 text-center text-bl-textSoft">Chargement…</div>;
  }
  if (!term) {
    return (
      <div className="max-w-editorial mx-auto px-6 py-32 text-center" data-testid="term-not-found">
        <p className="font-serif text-3xl">Terme introuvable</p>
        <Link to="/" className="overline mt-6 inline-block">Retour</Link>
      </div>
    );
  }

  return (
    <div className="fade-in-up" data-testid={`term-page-${term.slug}`}>
      {/* HERO */}
      <section className="border-b border-bl-border bg-bl-soft">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="overline" data-testid="breadcrumb-home">Glossaire</Link>
            <span className="text-bl-textSoft">/</span>
            {category && (
              <Link to={`/categorie/${category.slug}`} className="overline" data-testid="breadcrumb-category">
                {category.name}
              </Link>
            )}
          </div>
          <h1 className="font-serif font-light text-5xl md:text-7xl mt-8 text-bl-text leading-[1.05] text-balance">
            {term.name}
          </h1>
          <p className="font-serif italic text-xl md:text-2xl text-bl-textSoft mt-6 max-w-3xl leading-relaxed">
            {term.short_definition}
          </p>
          <button
            onClick={() => setQuizOpen(true)}
            className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-bl-dore hover:text-bl-text transition-colors border-b border-bl-doreLight pb-1"
            data-testid="open-quiz-btn"
          >
            <Sparkles size={12} strokeWidth={1.5} />
            Faire le quiz · Quel produit me correspond ?
          </button>
        </div>
      </section>

      {/* DEFINITION */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24 grid md:grid-cols-12 gap-10" data-testid="term-content">
        <aside className="md:col-span-4">
          <span className="overline">Définition</span>
          <h2 className="font-serif text-3xl mt-3 leading-tight">L'esprit Beckyliss</h2>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-bl-dore hover:text-bl-text transition-colors disabled:opacity-50"
            data-testid="regenerate-definition-btn"
          >
            <RefreshCw size={12} strokeWidth={1.5} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Régénération…" : "Régénérer (IA)"}
          </button>
        </aside>
        <article className="md:col-span-8">
          <p className="text-lg md:text-xl font-light text-bl-text leading-relaxed text-balance">
            {term.long_definition}
          </p>

          <div className="grid sm:grid-cols-2 gap-8 mt-12">
            {term.benefits?.length > 0 && (
              <div>
                <span className="overline flex items-center gap-2"><Sparkles size={12} /> Bénéfices</span>
                <ul className="mt-4 space-y-2">
                  {term.benefits.map((b) => (
                    <li key={b} className="flex gap-3 text-sm text-bl-text">
                      <span className="text-bl-dore">—</span> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {term.usage && (
              <div>
                <span className="overline">Mode d'utilisation</span>
                <p className="mt-4 text-sm text-bl-text leading-relaxed">{term.usage}</p>
              </div>
            )}
            {term.key_ingredients?.length > 0 && (
              <div>
                <span className="overline">Ingrédients clés</span>
                <p className="mt-4 text-sm text-bl-text">{term.key_ingredients.join(" · ")}</p>
              </div>
            )}
            {term.concerns?.length > 0 && (
              <div>
                <span className="overline">Préoccupations</span>
                <p className="mt-4 text-sm text-bl-text">{term.concerns.join(" · ")}</p>
              </div>
            )}
          </div>
        </article>
      </section>

      {/* RELATED PRODUCTS */}
      {products.length > 0 && (
        <section className="bg-bl-soft border-t border-bl-border py-20 md:py-24" data-testid="term-related-products">
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-baseline justify-between mb-12">
              <div>
                <span className="overline">Produits Beckyliss</span>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">À découvrir</h2>
              </div>
              <span className="overline">{products.length} pièces</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-16">
        {category && (
          <Link
            to={`/categorie/${category.slug}`}
            className="inline-flex items-center gap-2 text-sm overline"
            data-testid="back-to-category"
          >
            <ChevronLeft size={14} /> Retour à {category.name}
          </Link>
        )}
      </section>

      <RoutineBuilder termName={term.name} products={routine} />

      {quizOpen && <Quiz onClose={() => setQuizOpen(false)} />}
    </div>
  );
}
